import { useEffect, useState } from "react";
import {
  AlertOctagon,
  ArrowDownLeft,
  CreditCard,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { formatMoneyMinor } from "@/lib/money";
import { PageLoader } from "@/components/brand/PageLoader";
import { Button } from "@/components/ui/Button";

interface Stats {
  currency: string;
  window_days: number;
  totals: {
    charges_minor: number;
    refunded_minor: number;
    platform_fees_minor: number;
    platform_fees_refunded_minor: number;
    net_platform_fees_minor: number;
    open_disputes: number;
  };
  recent_charges: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    captured: boolean;
    paid: boolean;
    amount_refunded: number;
    created: number;
    receipt_email: string | null;
    on_behalf_of: string | null;
    description: string | null;
  }>;
  recent_refunds: Array<{
    id: string;
    amount: number;
    currency: string;
    reason: string | null;
    status: string;
    created: number;
  }>;
  open_disputes: Array<{
    id: string;
    amount: number;
    currency: string;
    reason: string;
    status: string;
    created: number;
    evidence_due_by: number | null;
  }>;
}

/**
 * Admin → Payments. Reads platform-wide Stripe activity (last 30 days)
 * via the stripe-platform-stats Edge Function. Headline cards show
 * gross charges, refunds, our application-fee revenue, and open
 * disputes; below them are short tables of recent charges, refunds,
 * and disputes-needing-response.
 *
 * Deliberately a snapshot, not a live ticker - re-render on tab
 * switch is plenty for now. Heavier reporting (per-venue breakdowns,
 * historical trends) belongs in a dedicated analytics tool, not the
 * admin panel.
 */
export function AdminPayments() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError(null);
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) {
      setError("Session expired - refresh the page.");
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${env.supabaseUrl}/functions/v1/stripe-platform-stats`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.error ?? `Stripe stats failed (${res.status})`);
        setStats(null);
        return;
      }
      setStats(json as Stats);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
            Admin · Payments
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Payments</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Last 30 days of Stripe activity across the whole platform.
            Numbers come straight from Stripe - match against the
            Audit log if anything looks off.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Refreshing…
            </>
          ) : (
            <>Refresh</>
          )}
        </Button>
      </header>

      {error && (
        <div className="mb-6 rounded-2xl border border-[#f87171]/30 bg-[rgba(248,113,113,0.05)] p-5 text-sm">
          <p className="font-semibold">Couldn't load payment data</p>
          <p className="mt-1 text-muted-foreground">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError(null);
              load();
            }}
            className="mt-3"
          >
            Try again
          </Button>
        </div>
      )}

      {!stats && loading && <PageLoader />}

      {stats && (
        <>
          {/* Headline numbers */}
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<CreditCard className="h-4 w-4" />}
              label="Gross charges"
              value={formatMoneyMinor(stats.totals.charges_minor, stats.currency)}
              hint={`${stats.window_days}-day window`}
            />
            <StatCard
              icon={<ArrowDownLeft className="h-4 w-4" />}
              label="Refunded"
              value={formatMoneyMinor(stats.totals.refunded_minor, stats.currency)}
              hint="Across captured charges"
              tone="muted"
            />
            <StatCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Net Whistll fees"
              value={formatMoneyMinor(
                stats.totals.net_platform_fees_minor,
                stats.currency,
              )}
              hint={`${formatMoneyMinor(
                stats.totals.platform_fees_minor,
                stats.currency,
              )} collected · ${formatMoneyMinor(
                stats.totals.platform_fees_refunded_minor,
                stats.currency,
              )} reversed`}
              tone="volt"
            />
            <StatCard
              icon={<AlertOctagon className="h-4 w-4" />}
              label="Open disputes"
              value={String(stats.totals.open_disputes)}
              hint={
                stats.totals.open_disputes > 0
                  ? "Each one needs evidence in Stripe"
                  : "No disputes need attention"
              }
              tone={stats.totals.open_disputes > 0 ? "danger" : "default"}
            />
          </div>

          {/* Recent charges */}
          <DataSection title="Recent charges" emptyLabel="No charges yet">
            {stats.recent_charges.length === 0 ? null : (
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Amount</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Customer</th>
                    <th className="px-4 py-2.5">Connected acct</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_charges.map((c) => (
                    <tr key={c.id} className="border-b border-border/40 last:border-b-0">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {new Date(c.created * 1000).toLocaleString(undefined, {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-2.5 font-semibold">
                        {formatMoneyMinor(c.amount, c.currency)}
                        {c.amount_refunded > 0 && (
                          <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                            (−{formatMoneyMinor(c.amount_refunded, c.currency)})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <ChargeStatusPill
                          status={c.status}
                          captured={c.captured}
                          refunded={c.amount_refunded}
                          amount={c.amount}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {c.receipt_email ?? "-"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                        {c.on_behalf_of ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DataSection>

          {/* Open disputes */}
          {stats.open_disputes.length > 0 && (
            <DataSection title="Open disputes" emptyLabel="None">
              <ul className="space-y-2 p-4">
                {stats.open_disputes.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-col gap-1 rounded-lg border border-[#f87171]/30 bg-[rgba(248,113,113,0.05)] p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-semibold">
                        {formatMoneyMinor(d.amount, d.currency)} ·{" "}
                        <span className="text-muted-foreground">{d.reason}</span>
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        Status: {d.status.replace(/_/g, " ")}
                        {d.evidence_due_by
                          ? ` · evidence due ${new Date(
                              d.evidence_due_by * 1000,
                            ).toLocaleDateString()}`
                          : ""}
                      </p>
                    </div>
                    <a
                      href={`https://dashboard.stripe.com/disputes/${d.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-bold uppercase tracking-wider text-volt underline-offset-2 hover:underline"
                    >
                      Open in Stripe
                    </a>
                  </li>
                ))}
              </ul>
            </DataSection>
          )}

          {/* Recent refunds */}
          {stats.recent_refunds.length > 0 && (
            <DataSection title="Recent refunds" emptyLabel="None">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Amount</th>
                    <th className="px-4 py-2.5">Reason</th>
                    <th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_refunds.map((r) => (
                    <tr key={r.id} className="border-b border-border/40 last:border-b-0">
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {new Date(r.created * 1000).toLocaleString(undefined, {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-2.5 font-semibold">
                        {formatMoneyMinor(r.amount, r.currency)}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">
                        {r.reason ?? "-"}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataSection>
          )}

          <p className="mt-8 text-xs text-muted-foreground">
            Need detail on a specific transaction? Open it in the Stripe
            dashboard with the charge / refund / dispute id above. Bigger
            reports (per-venue, historical trends) live there too.
          </p>
        </>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "muted" | "volt" | "danger";
}) {
  const accent =
    tone === "volt"
      ? "text-volt"
      : tone === "danger"
        ? "text-[#f87171]"
        : tone === "muted"
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className={`mb-2 inline-flex items-center gap-2 ${accent}`}>
        {icon}
        <span className="text-[11px] font-bold uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className={`text-2xl font-semibold ${accent}`}>{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function DataSection({
  title,
  emptyLabel,
  children,
}: {
  title: string;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      <div className="rounded-2xl border border-border bg-card">
        {/* Inner overflow-x-auto so the rounded corners stay sharp
            but wide tables can scroll horizontally on mobile. */}
        <div className="overflow-x-auto">
          {children ?? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              {emptyLabel}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function ChargeStatusPill({
  status,
  captured,
  refunded,
  amount,
}: {
  status: string;
  captured: boolean;
  refunded: number;
  amount: number;
}) {
  if (status === "succeeded" && refunded >= amount) {
    return (
      <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
        refunded
      </span>
    );
  }
  if (status === "succeeded" && refunded > 0) {
    return (
      <span className="rounded-full bg-[rgba(251,191,36,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#fbbf24]">
        partial refund
      </span>
    );
  }
  if (status === "succeeded" && captured) {
    return (
      <span className="rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
        captured
      </span>
    );
  }
  if (status === "succeeded" && !captured) {
    return (
      <span className="rounded-full bg-[rgba(251,191,36,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#fbbf24]">
        authorized
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="rounded-full bg-[rgba(248,113,113,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#f87171]">
        failed
      </span>
    );
  }
  return (
    <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
      {status}
    </span>
  );
}
